import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp,
  Lock,
  Unlock,
  Wallet,
  Sparkles,
  Calendar,
  ArrowDownToLine,
  RefreshCw,
  Plus,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const MyInvestments = () => {
  const [investments, setInvestments] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const [invRes, balRes] = await Promise.all([
        axios.get(`${API_URL}/api/investments/me`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/investments/me/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setInvestments(invRes.data);
      setBalance(balRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAction = async (investmentId, action) => {
    const token = localStorage.getItem('token');
    setActionId(`${investmentId}-${action}`);
    try {
      const res = await axios.post(
        `${API_URL}/api/investments/${investmentId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const successMsg = {
        claim: `Reclamado: $${res.data.claimed?.toFixed(2)}`,
        compound: `Capitalizado: $${res.data.compounded?.toFixed(2)}`,
        withdraw: `Retirado: $${res.data.withdrawn?.toFixed(2)}`,
      }[action];
      toast.success(successMsg);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la operacion');
    } finally {
      setActionId(null);
    }
  };

  const calcAccrued = (inv) => {
    if (inv.status !== 'active') return 0;
    const monthlyYield = inv.principal * (inv.monthly_yield_pct / 100);
    const baseDate = new Date(inv.last_payout_at || inv.started_at);
    const daysElapsed = (Date.now() - baseDate.getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, monthlyYield * (daysElapsed / 30));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const totalAccrued = investments
    .filter((i) => i.status === 'active')
    .reduce((s, i) => s + calcAccrued(i), 0);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-6" data-testid="my-investments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Mis Inversiones</h1>
          <p className="text-sm text-white/50 mt-1">
            Capital depositado, rendimientos y opciones de retiro
          </p>
        </div>
        <Link to="/packages">
          <Button
            data-testid="new-investment-btn"
            className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold hover:from-amber-400 hover:to-amber-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Inversion
          </Button>
        </Link>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-5 h-5 text-amber-400" />
              <Badge variant="outline" className="text-xs">
                Capital
              </Badge>
            </div>
            <p data-testid="total-invested" className="text-2xl font-bold">
              ${balance?.total_invested?.toLocaleString() || '0'}
            </p>
            <p className="text-xs text-white/40 mt-1">Total invertido</p>
          </CardContent>
        </Card>

        <Card className="border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <Badge variant="outline" className="text-xs">
                Yield
              </Badge>
            </div>
            <p data-testid="total-yield" className="text-2xl font-bold text-emerald-400">
              ${balance?.total_yield_earned?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/40 mt-1">Ganado total</p>
          </CardContent>
        </Card>

        <Card className="border-blue-500/20 bg-gradient-to-br from-blue-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <ArrowDownToLine className="w-5 h-5 text-blue-400" />
              <Badge variant="outline" className="text-xs">
                Reclamable
              </Badge>
            </div>
            <p data-testid="claimable-balance" className="text-2xl font-bold text-blue-400">
              ${balance?.claimable_usd?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-white/40 mt-1">Listo para retirar</p>
          </CardContent>
        </Card>

        <Card className="border-violet-500/20 bg-gradient-to-br from-violet-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              <Badge variant="outline" className="text-xs">
                Pendiente
              </Badge>
            </div>
            <p data-testid="pending-yield" className="text-2xl font-bold text-violet-400">
              ${totalAccrued.toFixed(2)}
            </p>
            <p className="text-xs text-white/40 mt-1">Acumulado este mes</p>
          </CardContent>
        </Card>
      </div>

      {/* Investments list */}
      {investments.length === 0 ? (
        <Card className="border-amber-500/20 border-dashed">
          <CardContent className="p-12 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
              <Wallet className="w-8 h-8 text-amber-400" />
            </div>
            <h3 className="text-xl font-semibold">No tienes inversiones aun</h3>
            <p className="text-white/50 max-w-sm mx-auto">
              Comienza a generar rendimientos depositando en uno de nuestros 3 paquetes.
            </p>
            <Link to="/packages">
              <Button className="bg-amber-500 text-black hover:bg-amber-400">
                Ver Paquetes
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {investments.map((inv) => {
            const accrued = calcAccrued(inv);
            const isLocked =
              inv.lock_until && new Date(inv.lock_until).getTime() > Date.now();
            const Icon = inv.lock_months === 0 ? Unlock : inv.lock_months === 6 ? Lock : Sparkles;
            return (
              <Card
                key={inv.id}
                data-testid={`investment-row-${inv.id}`}
                className={cn(
                  'border bg-zinc-900/60 transition-all',
                  inv.status === 'active' ? 'border-amber-500/20' : 'border-zinc-700/50 opacity-60'
                )}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Header info */}
                    <div className="flex items-center gap-3 flex-1">
                      <div
                        className={cn(
                          'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                          inv.lock_months === 0
                            ? 'bg-amber-500/10 text-amber-400'
                            : inv.lock_months === 6
                            ? 'bg-blue-500/10 text-blue-400'
                            : 'bg-emerald-500/10 text-emerald-400'
                        )}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold">{inv.package_name}</h3>
                          <Badge
                            className={cn(
                              'text-xs',
                              inv.status === 'active'
                                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                                : 'bg-zinc-700/50 text-zinc-400 border-zinc-600'
                            )}
                          >
                            {inv.status}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {inv.currency}
                          </Badge>
                        </div>
                        <p className="text-xs text-white/40 mt-1">
                          Iniciado: {formatDate(inv.started_at)}
                          {isLocked && (
                            <>
                              {' • '}
                              <span className="text-amber-400">
                                Desbloqueo: {formatDate(inv.lock_until)}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 lg:gap-6 lg:min-w-[400px]">
                      <div>
                        <div className="text-xs text-white/40">Capital</div>
                        <div className="font-mono font-semibold">
                          ${inv.principal.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-white/40">Yield/mes</div>
                        <div className="font-mono font-semibold text-amber-400">
                          {inv.monthly_yield_pct}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-white/40">Pendiente</div>
                        <div className="font-mono font-semibold text-emerald-400">
                          +${accrued.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {inv.status === 'active' && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid={`claim-btn-${inv.id}`}
                          size="sm"
                          variant="outline"
                          disabled={accrued <= 0 || actionId !== null}
                          onClick={() => handleAction(inv.id, 'claim')}
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        >
                          {actionId === `${inv.id}-claim` ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <ArrowDownToLine className="w-3.5 h-3.5" />
                          )}
                          <span className="ml-1.5">Reclamar</span>
                        </Button>
                        <Button
                          data-testid={`compound-btn-${inv.id}`}
                          size="sm"
                          variant="outline"
                          disabled={accrued <= 0 || actionId !== null}
                          onClick={() => handleAction(inv.id, 'compound')}
                          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        >
                          {actionId === `${inv.id}-compound` ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3.5 h-3.5" />
                          )}
                          <span className="ml-1.5">Capitalizar</span>
                        </Button>
                        {!isLocked && (
                          <Button
                            data-testid={`withdraw-btn-${inv.id}`}
                            size="sm"
                            variant="outline"
                            disabled={actionId !== null}
                            onClick={() => handleAction(inv.id, 'withdraw')}
                            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                          >
                            Retirar
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyInvestments;
